import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { toast } from "react-toastify";
import bgImage from "../Images/bg.jpg"; // Update path based on your structure
import ReactLoading from "react-loading";
import {jwtDecode} from "jwt-decode";
import defaultWallpaper from "../Images/bg.jpg"; 
import axios from "axios";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [orgLogo, setOrgLogo] = useState(null); // <-- state for logo
  const [wallpaper, setWallpaper] = useState(""); 
  const [isLoading, setIsLoading] = useState(false);

  const validateToken = useCallback(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    try{
      const decodeToken = jwtDecode(token);
      const expiry = decodeToken.exp * 1000;

      if (!expiry || Date.now() > expiry) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("tokenExpiry");
      }
    }catch (e) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("tokenExpiry");
      console.warn("Malformed token removed from storage.");
    }
  }, []);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // const logout = useCallback(() => {
  //   sessionStorage.clear();
  //   toast.info("Session expired. Please log in again.");
  //   navigate("/login");
  // }, [navigate]);

   useEffect(() => {
     let cancelled = false;
     const fetchWallpaper = async () => {
      try {
        // const orgId = "68aedf6528d41743b0b68f58";
        const res = await fetch(`${API_BASE_URL}/organizations/login-theme`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          console.info("login-theme fetch failed:", body.message || res.status);
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        setWallpaper(data.organization_wallpaper || "");
        setOrgLogo(data.organization_logo || "");
      } catch (err) {
        console.error("Error fetching wallpaper:", err);
      }
    };

    fetchWallpaper();
    return () => cancelled = true;
  }, []);


  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "username") setUsername(value);
    if (name === "password") setPassword(value);
  };

  const handleCheckboxChange = () => {
    setRememberMe((prev) => !prev);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true); 

    try {
      const response = await fetch(`${API_BASE_URL}/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.token) {
        setError(data.message || "Login failed");
        toast.error(data.message || "Login failed. Please try again.");
        setIsLoading(false); // Hide loading after error
        return;
      }
      
      let decodedToken;
      try{
        decodedToken = jwtDecode(data.token);
      }catch (e) {
        toast.error("Received invalid token from server. Contact admin.");
        setIsLoading(false);
        return;
      }
      const tokenExpiry = decodedToken.exp ? decodedToken.exp * 1000 : Date.now() + 3600 * 1000;

      // Store token and user data in sessionStorage
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.data));
      sessionStorage.setItem("role", data.data.role);
      sessionStorage.setItem("user_code", data.data.user_code);
      sessionStorage.setItem("username", data.data.username);
      sessionStorage.setItem("location", data.data.location);
      sessionStorage.setItem("organization", data.data.organization);
      sessionStorage.setItem("tokenExpiry", tokenExpiry);

      toast.success("Login successful!");

      if (data.data.role === "Main Admin") {
        navigate("/mainadmin-dashboard");
      } else if (data.data.role === "Company Admin") {
        navigate("/companyadmin-dashboard");
      } else {
        navigate("/user-dashboard");
      }
    } catch (error) {
      console.error("Error logging in:", error);
      setError("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false); 
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Background Image */}
      <div
        className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage: `url(${wallpaper && wallpaper !== "" ? wallpaper : defaultWallpaper})`,
        }}
      >

        {/* Titles over the background */}
        <div className="flex items-center justify-left w-full h-full">
          <div
            className="absolute left-0 md:left-10 top-1/5 md:top-1/5 bg-black bg-opacity-50 p-10 rounded-lg shadow-lg text-white text-center"
            style={{ width: "50%" }}
          >
            {/* Content */}
            <div className="flex flex-col items-center space-y-4">
              <div className="text-5xl font-semibold">Farm Management System</div>
              <img
                src={orgLogo ? orgLogo : "/agro_farm_ims_logo.png"} // fallback image
                alt="Profile"
                className="max-h-[240px] max-w-[240px] object-contain"
              />
              <p className="text-base font-semibold text-2sm">
                Welcome to Farm Management System, your trusted solution for managing
                your agricultural business efficiently. Stay organized and
                streamline your operations with ease!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Login Form */}
      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-sm p-8 bg-[rgba(255,255,255,0.3)] shadow-lg rounded-lg absolute right-10 top-1/2 transform -translate-y-1/2">
          <div className="text-center mb-6">
            <img
              src={orgLogo ? orgLogo : "/agro_farm_ims_logo.png"} // fallback image
              alt="Profile"
              className="mx-auto max-h-[200px] max-w-[200px]"
            />
            <h1 className="text-2xl font-semibold mt-4">Welcome Back</h1>
            <p className="text-gray-800 mt-2">Sign in to continue</p>
          </div>
          <Form className="space-y-4" onSubmit={handleSubmit}>
            {error && <p className="text-red-500">{error}</p>}
            <Form.Group className="relative">
              <i className="fas fa-user absolute top-3 left-3 text-gray-400"></i>
              <Form.Control
                type="text"
                placeholder="Username"
                name="username"
                value={username}
                onChange={handleChange}
                className="pl-10 w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </Form.Group>
            <Form.Group className="relative">
              <i className="fas fa-lock absolute top-3 left-3 text-gray-400"></i>
              <Form.Control
                type="password"
                placeholder="Password"
                name="password"
                value={password}
                onChange={handleChange}
                className="pl-10 w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </Form.Group>
            <button
              type="submit"
              className="w-full py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Sign In
            </button>
            {/* Loading Spinner below the button */}
            {isLoading && (
              <div className="flex justify-center mt-4">
                <ReactLoading
                  type="cubes"
                  color="#FFFFFF"
                  height={30}
                  width={30}
                />
              </div>
            )}
            <div className="flex items-center justify-between text-sm text-gray-800 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="keep-me-signed-in"
                  checked={rememberMe}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-4 w-4 text-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="keep-me-signed-in" className="ml-2">
                  Keep me signed in
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-blue-300 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <div className="text-center text-sm text-gray-800 mt-4">
              Don't have an account?{" "}
              <Link to="/register" className="text-blue-300 hover:underline">
                Create one
              </Link>
            </div>
          </Form>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-m font-semibold text-dark">
        Developed By Tokyo Cement IT Team.Copyrights.2024
      </div>
    </div>
  );
}

export default Login;
